import { generateHTML, getCaretPosition } from "../helpers"
import { normalizeName } from "../../../searchprotocol"
import { ParsedElement, makeParsedElement, parse } from "../parsing"
import { SearchInputComponent } from "../../components/SearchInput"

const wrapAround = (position: number, size: number): number =>
    (position + size) % size

// given it's position, finds where caret would be located in the parsed result
function calculateCaretLocation(
    elements: ParsedElement[],
    position: number,
): Search.CalculatedCaretLocation {
    let targetIndex = 0
    for (let i = 0; i < elements.length; i++) {
        const element = elements[i]
        const length = element.asString.length
        if (length >= position) {
            break
        }
        targetIndex = i
        position -= length
    }
    if (
        elements[targetIndex].type === "semicolon" &&
        targetIndex != elements.length - 1
    ) {
        targetIndex++
    }

    return {
        elementIndex: targetIndex,
        relativeOffset: position,
    }
}

function getSuggestions(
    query: string,
    fieldLabels: Search.NormalizedNameLUT,
): Search.Suggestion[] {
    if (query[0] === "+" || query[0] === "-") {
        query = query.slice(1)
    }
    query = normalizeName(query)
    return Object.entries(fieldLabels)
        .map(([normalized, field]) => ({
            field,
            score: normalized.indexOf(query),
            normalized,
        }))
        .filter((ranked) => ranked.score >= 0)
        .sort((a, b) => a.score - b.score)
        .map((ranked) => ({ ...ranked.field, normalized: ranked.normalized }))
        .slice(0, 10) as Search.Suggestion[]
}

export function clearSuggestions(this: SearchInputComponent): void {
    this.setState({
        focusedSuggestionCode: null,
        suggestions: [],
    })
}

export function focusOnSuggestion(
    this: SearchInputComponent,
    focusedSuggestionCode: string | null,
): void {
    this.setState({ focusedSuggestionCode })
}

export function getFocusedSuggestion(
    this: SearchInputComponent,
): Search.Suggestion | null {
    const { suggestions } = this.state
    const { focusedSuggestionCode } = this.state
    if (focusedSuggestionCode === null) {
        return null
    }

    return (
        suggestions.find(
            (suggestion) => suggestion.normalized === focusedSuggestionCode,
        ) || null
    )
}

export function handleSelectSuggestion(
    this: SearchInputComponent,
    selectedSuggestion: Search.Suggestion,
): void {
    const fieldLabels = this.props.normalizedNameLUT
    const currentInputText = this.getInputText()
    const position = getCaretPosition(this.input.current)
    const beforeLength = currentInputText.length
    let fieldName = selectedSuggestion.original
    if (!position) {
        return
    }

    // find the corresponding location of the caret
    const parsedElements = parse(currentInputText, fieldLabels)
    const targetIndex = calculateCaretLocation(
        parsedElements,
        position,
    ).elementIndex
    const targetParsedElement = parsedElements[targetIndex]
    const targetParsedElementValue =
        targetParsedElement.type === "keyValue"
            ? targetParsedElement.value
            : undefined
    const firstChar = targetParsedElement.asString[0]
    if (firstChar === "+" || firstChar === "-") {
        fieldName = firstChar + fieldName
    }

    // inject text from suggestion into parsed result
    parsedElements.splice(
        targetIndex,
        1,
        makeParsedElement(fieldName, targetParsedElementValue),
    )
    this.oneTimeCaretShiftAmount--
    if (
        targetParsedElement.type !== "keyValue" &&
        parsedElements[targetIndex + 1]?.type !== "colon"
    ) {
        this.oneTimeCaretShiftAmount++
        parsedElements.splice(targetIndex + 1, 0, makeParsedElement(":"))
    }

    const modified = parsedElements.map((x) => x.asString).join("")
    const generated = generateHTML(modified, fieldLabels)
    this.setState({
        html: generated.html,
    })

    this.oneTimeCaretShiftAmount += beforeLength - generated.selectableLength
    this.clearSuggestions()
}

export function loadSuggestions(this: SearchInputComponent): void {
    this.setState({ suggestions: [] })
    const input = this.input.current
    const text = this.getInputText()
    const position = getCaretPosition(input)
    if (!position) {
        return
    }
    const parsedElements = parse(text, this.props.normalizedNameLUT)
    const { elementIndex, relativeOffset } = calculateCaretLocation(
        parsedElements,
        position,
    )
    const target = parsedElements[elementIndex]
    let query = ""

    // is caret at the end of the key or text element?
    if (target.type === "text" && target.text.length === relativeOffset) {
        query = target.text
    } else if (
        target.type === "keyValue" &&
        target.key.length === relativeOffset
    ) {
        query = target.key
    } else {
        return
    }

    const suggestions = getSuggestions(query, this.props.normalizedNameLUT)
    this.setState({ suggestions })
}

export function onKeyDownInput(
    this: SearchInputComponent,
    event: React.KeyboardEvent,
): void {
    const { focusedSuggestionCode, suggestions } = this.state

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        if (!suggestions.length) {
            return
        }
        const direction = event.key === "ArrowDown" ? 1 : -1
        if (!focusedSuggestionCode) {
            return this.focusOnSuggestion(
                suggestions[direction == 1 ? 0 : suggestions.length - 1]
                    .normalized,
            )
        }

        const nextFocusedSuggestionIndex = wrapAround(
            suggestions.findIndex(
                (suggestion) => suggestion.normalized === focusedSuggestionCode,
            ) + direction,
            suggestions.length,
        )
        this.focusOnSuggestion(
            suggestions[nextFocusedSuggestionIndex].normalized,
        )
    }
    if (["ArrowRight", "Tab", "Enter"].includes(event.key)) {
        const isEnter = event.key === "Enter"
        if (!suggestions.length) {
            isEnter && this.doSearch()
            return
        }
        event.preventDefault()
        const focusedSuggestion = this.getFocusedSuggestion()
        if (!focusedSuggestion && isEnter) {
            this.doSearch()
            return
        }

        return this.handleSelectSuggestion(focusedSuggestion || suggestions[0])
    }
    if (event.key === "Escape") {
        this.clearSuggestions()
        return
    }
}

export function onSuggestionClick(
    this: SearchInputComponent,
    selectedSuggestion: Search.Suggestion,
): void {
    this.handleSelectSuggestion(selectedSuggestion)
}
